import { useMemo, useState } from 'react';

import {
  useLegalClientCreateHead,
  useLegalClientHeadSavePersonalData,
} from 'api/requests';
import { ErrorCode } from 'api/types/error';
import { useNotify } from 'libs/notify';
import {
  PersonalDataFormType,
  FormErrors,
} from 'modules/onboarding/common/forms/personalDataForm';

import { useOnboardingContext } from './useOnboardingContext';

export const usePersonalData = () => {
  const { goToBack, goToNext, data } = useOnboardingContext();
  const notify = useNotify();

  const [submitLoading, setSubmitLoading] = useState(false);

  const {
    savePersonalData,
    loading: submitPersonalDataLoading,
    error: errorPersonalData,
  } = useLegalClientHeadSavePersonalData();

  const { createHead, error: createError } = useLegalClientCreateHead();

  const initialValues = useMemo<PersonalDataFormType | undefined>(
    () =>
      data.legalClientHead
        ? {
            email: data.legalClientHead.email,
            firstName: data.legalClientHead.firstName,
            lastName: data.legalClientHead.lastName,
            dateOfBirth: data.legalClientHead.dateOfBirth,
            middleName: data.legalClientHead.middleName,
          }
        : undefined,
    [data.legalClientHead]
  );

  const onSubmit = async (values: PersonalDataFormType) => {
    if (values) {
      if (!data.legalClientHead) {
        setSubmitLoading(true);

        try {
          const res = await createHead(data.legalClient.id!, values);
          if (res) {
            data.onCreateHead(res.id);
            goToNext();
          }
        } catch (error) {
          setSubmitLoading(false);
        }
      } else {
        const head = await savePersonalData(
          data.legalClient.id,
          data.legalClientHead.id,
          values
        );

        if (head) {
          goToNext();
        }
      }
    }
  };

  const formError = useMemo((): FormErrors | undefined => {
    if (createError?.code === ErrorCode.USER_WITH_EMAIL_ALREADY_EXISTS) {
      return { email: createError?.message };
    }

    if (errorPersonalData?.code === ErrorCode.USER_WITH_EMAIL_ALREADY_EXISTS) {
      return { email: errorPersonalData?.message };
    }

    if (createError || errorPersonalData) {
      notify.error(createError ?? errorPersonalData);
    }

    return undefined;
  }, [createError, errorPersonalData, notify]);

  return {
    onSubmit,
    goToBack,
    initialValues,
    formError,
    submitLoading: submitLoading || submitPersonalDataLoading,
  };
};
